import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { DataNode } from "antd/lib/tree";
import { viewsStore } from "./ViewsStore";
import { getUid } from "utils/uid";

export interface ExtendedDataNode extends DataNode, Pick<API_ViewNavigationEntry, "metadata"> {
  // hasChildren?: boolean;
  children?: ExtendedDataNode[];
  navigationId: number;
  isNavigation?: boolean; // to handle render of edit and delete buttons in sidebar
}

function viewNavigationEntryToDataNode(item: API_ViewNavigationEntry, navigationId: number): ExtendedDataNode {
  return {
    key: getUid(),
    title: item.name,
    isLeaf: !item.subentries,
    metadata: item.metadata,
    navigationId: navigationId,
  };
}

function viewNavigationToDataNode(item: API_ViewNavigation): ExtendedDataNode {
  return {
    key: item.id,
    title: item.name,
    children: item.data.map((entry) => viewNavigationEntryToDataNode(entry, item.id)),
    navigationId: item.id,
    isNavigation: true,
    isLeaf: !item.data.length,
  };
}

// Used in Documents module to show folders structure available for all project members.
export class ViewsFoldersStore extends CoreStore {
  deleteState: StoreState = "RAW";
  dbData: API_ViewNavigation[] = [];
  data: ExtendedDataNode = { key: "null", navigationId: undefined };
  expandedKeysMap: Map<number | string, number | string> = new Map();

  constructor() {
    super();

    makeObservable(this, {
      deleteState: observable,
      dbData: observable.shallow,
      data: observable.shallow,
      expandedKeysMap: observable,
      clear: action.bound,
      setExpandedKey: action.bound,
      createNavigation: action.bound,
      editNavigation: action.bound,
      fetchFolder: action.bound,
      getNavigationEntry: action.bound,
      rootFolderId: computed,
      expandedKeys: computed,
      treeData: computed,
      fetch: action.bound,
      deleteNavigation: action.bound,
    });
  }

  url(id?: number) {
    if (id) {
      return API_URL.VIEWS.getViewNavigationEntries(projectsDataStore.projectId, viewsStore.currentViewId, id);
    }
    return API_URL.VIEWS.getViewNavigation(projectsDataStore.projectId, viewsStore.currentViewId);
  }

  clear() {
    this.deleteState = "RAW";
    this.dbData = [];
    this.data = { key: "null", navigationId: undefined };
    this.expandedKeysMap = new Map();
  }

  setExpandedKey(key: string | number, expand: boolean) {
    if (expand) {
      this.expandedKeysMap.set(key, key);
    } else {
      this.expandedKeysMap.delete(key);
    }
  }

  @handleStoreState()
  async fetch() {
    try {
      const response: API_ViewNavigation[] = await API.getData(this.url());
      runInAction(() => {
        const normalizedResponse = response.map(viewNavigationToDataNode);
        this.dbData = response;
        this.data = { key: "null", navigationId: undefined, title: "", children: normalizedResponse };
        this.setExpandedKey("null", true);
        // if (id !== this.rootFolderId) {
        //   this.findInTree(this.data.subfolders, id);
        // }
      });
    } catch (error) {
      throw error;
    }
  }

  async createNavigation(formData: API_ViewNavigation) {
    try {
      await API.newData(
        API_URL.VIEWS.createViewNavigation(projectsDataStore.projectId, viewsStore.currentViewId),
        formData
      );
      this.fetch();
    } catch (error) {
      throw error;
    }
  }

  async editNavigation(formData: API_ViewNavigation) {
    try {
      await API.editData(
        API_URL.VIEWS.updateViewNavigation(projectsDataStore.projectId, viewsStore.currentViewId, formData.id),
        formData
      );
      this.fetch();
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState<ViewsFoldersStore>("deleteState")
  async deleteNavigation(id: number) {
    try {
      await API.deleteData(
        API_URL.VIEWS.deleteViewNavigation(projectsDataStore.projectId, viewsStore.currentViewId, id)
      );
      this.fetch();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Finds seeking folder, requests it's subfolder and injects it's content into
   */
  async fetchFolder(folderKey: string, forceUpdate = false) {
    try {
      const copy = { ...this.data };
      let foundItem = this.findInTree(copy.children, folderKey);
      if (!foundItem) {
        foundItem = copy;
      }
      if (forceUpdate || (!foundItem.isLeaf && !foundItem.children)) {
        const formData = { metadata: foundItem.metadata };
        const response: API_ViewNavigationEntry[] = await API.newData(
          API_URL.VIEWS.getViewNavigationEntries(
            projectsDataStore.projectId,
            viewsStore.currentViewId,
            foundItem.navigationId
          ),
          formData
        );
        runInAction(() => {
          foundItem.children = response.map((entry) => viewNavigationEntryToDataNode(entry, foundItem.navigationId));
          // foundItem.isLeaf = !response.children;
          this.data = copy;
        });
      }
    } catch (error) {
      throw error;
    }
  }

  getNavigationEntry(folderKey: string) {
    const foundItem = this.findInTree(this.data.children, folderKey);
    console.log(foundItem);
    // case for clicking on view navigation that does not have files in it (metadata is empty)
    if (!foundItem?.metadata) {
      return undefined;
    }
    return foundItem;
  }

  get rootFolderId() {
    return this.data.key;
  }

  get expandedKeys() {
    return Array.from(this.expandedKeysMap.keys());
  }

  get treeData() {
    return [this.data];
  }

  // Traverse through tree nodes to return reference of node by id
  private findInTree = (array: ExtendedDataNode[], key: string | number): ExtendedDataNode => {
    if (array && array.length) {
      let item: ExtendedDataNode;
      for (let i = 0; i < array.length; i++) {
        if (array[i].key === key) {
          item = array[i];
          break;
        } else if (array[i].children) {
          // in order to expand every folder on the way to seeked folder
          // this.setExpandedKey(array[i].key, true);
          const found = this.findInTree(array[i].children, key);
          if (found) {
            item = found;
          }
        }
      }
      return item;
    } else {
      return { key: null, navigationId: undefined };
    }
  };
}

export const viewsFoldersStore = new ViewsFoldersStore();
